import * as SupportActions from './support.actions';

import {createReducer, on} from '@ngrx/store';
import {SupportCategoryModel, SupportTicketMobileMessageModel, SupportTicketMobileModel} from '../_models/support.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {createEntityAdapter} from '@ngrx/entity';
import {DahLoadingType} from '@common/components-module/core-components.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';


export const supportFeatureKey = 'support';

export function sortMessagesByDate(a: SupportTicketMobileMessageModel, b: SupportTicketMobileMessageModel) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'date', 'ASC');
}

export const archivedTicketsAdapter = createEntityAdapter<SupportTicketMobileModel>({});

export const messagesAdapter = createEntityAdapter<SupportTicketMobileMessageModel>({
    sortComparer: sortMessagesByDate
});

export interface SupportState {
    tickets: {
        active?: SupportTicketMobileModel[],
        archive: EntityStateModel<SupportTicketMobileModel, null>,
        ticketCreating?: boolean
    },
    messages: Record<string, EntityStateModel<SupportTicketMobileMessageModel, null>>,
    categories?: SupportCategoryModel[],
}

const initialState: SupportState = {
    tickets: {
        archive: archivedTicketsAdapter.getInitialState({
            pagination: {size: 20},
            listLoading: undefined,
            itemsLoading: []
        })
    },
    messages: {}
};

const messagesInitialState = messagesAdapter.getInitialState({
    pagination: {number: 0, size: 30},
    listLoading: undefined,
    itemsLoading: []
});


export const supportReducer = createReducer(
    initialState,

    on(SupportActions.LoadCategoriesSuccess, (state, action) => ({
        ...state,
        categories: action.payload.response,
    })),


    on(SupportActions.LoadTickets, (state, action) => ({
        ...state,
        tickets: {
            ...state.tickets,
            archive: {
                ...state.tickets.archive,
                listLoading: action.payload.loading,
            }
        }
    })),

    on(SupportActions.LoadTicketsSuccess, (state, action) => ({
        ...state,
        tickets: {
            active: action.payload.response.active ?? [],
            archive: archivedTicketsAdapter.setMany(action.payload.response.archive?.content ?? [], {
                ...state.tickets.archive,
                pagination: StoredComponentHelperService.cleanPaginationResponse(action.payload.response.archive),
                listLoading: undefined,
            })
        }
    })),

    on(SupportActions.LoadTicketsFailure, (state) => ({
        ...state,
        tickets: {
            ...state.tickets,
            archive: {
                ...state.tickets.archive,
                listLoading: undefined,
            }
        }
    })),


    on(SupportActions.LoadArchiveTickets, (state) => ({
        ...state,
        tickets: {
            ...state.tickets,
            archive: {
                ...state.tickets.archive,
                pagination: {
                    ...state.tickets.archive.pagination,
                    number: StoredComponentHelperService.setNextPage(state.tickets.archive.pagination, state.tickets.archive.ids.length)
                },
                listLoading: 'listLoadNext' as DahLoadingType,
            }
        }
    })),

    on(SupportActions.LoadArchiveTicketsSuccess, (state, action) => ({
        ...state,
        tickets: {
            ...state.tickets,
            archive: archivedTicketsAdapter.upsertMany(action.payload.response.content!, {
                ...state.tickets.archive,
                pagination: StoredComponentHelperService.cleanPaginationResponse(action.payload.response),
                listLoading: undefined
            }),
        }
    })),

    on(SupportActions.LoadArchiveTicketsFailure, (state) => ({
        ...state,
        tickets: {
            ...state.tickets,
            archive: {
                ...state.tickets.archive,
                listLoading: undefined,
            }
        }
    })),


    on(SupportActions.SetNextPage, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...state.messages[action.payload.ticketId],
                pagination: {
                    ...state.messages[action.payload.ticketId].pagination,
                    number: state.messages[action.payload.ticketId].pagination.number! + 1
                }
            }
        }
    })),


    on(SupportActions.OpenMessages, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...messagesInitialState,
                ...state.messages[action.payload.ticketId],
            }
        }
    })),

    // on(SupportActions.LoadMessages, (state, action) => ({
    //     ...state,
    //     messages: {
    //         ...state.messages,
    //         [action.payload.ticketId]: {
    //             ...messagesInitialState,
    //             ...!action.payload.clearState && {...state.messages[action.payload.ticketId]},
    //             listLoading: 'listLoading' as DahLoadingType,
    //         }
    //     }
    // })),

    on(SupportActions.LoadMessagesSuccess, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: action.payload.clearState
                ? messagesAdapter.setMany(setImageDimsList(action.payload.response.content!), {
                    ...state.messages[action.payload.ticketId],
                    pagination: StoredComponentHelperService.cleanPaginationResponse(action.payload.response),
                    listLoading: undefined,
                }) : messagesAdapter.upsertMany(setImageDimsList(action.payload.response.content!), {
                    ...state.messages[action.payload.ticketId],
                    pagination: StoredComponentHelperService.cleanPaginationResponse(action.payload.response),
                    listLoading: undefined,
                })
        }
    })),

    on(SupportActions.LoadMessagesFailure, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...state.messages[action.payload.ticketId],
                listLoading: undefined,
            }
        }
    })),


    on(SupportActions.SendMessage, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...state.messages[action.payload.ticketId],
                listLoading: 'entityOpening' as DahLoadingType,
            }
        }
    })),

    on(SupportActions.SendMessageSuccess, (state, action) => {
        let newActive = state.tickets.active;

        const item = newActive?.find(item => item.id === action.payload.ticketId);
        if (item && newActive) {
            newActive = [
                { ...item, unread: false },
                ...newActive.filter(i => i.id !== action.payload.ticketId)
            ];
        }

        return {
            ...state,
            tickets: {
                ...state.tickets,
                active: newActive
            },
            messages: {
                ...state.messages,
                [action.payload.ticketId]: messagesAdapter.addOne(action.payload.message, {
                    ...state.messages[action.payload.ticketId],
                    listLoading: undefined,
                }),
            }
        };
    }),

    on(SupportActions.SendMessageFailure, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: {
                ...state.messages[action.payload.ticketId],
                listLoading: undefined,
            }
        }
    })),


    on(SupportActions.CreateTicket, (state) => ({
        ...state,
        tickets: {
            ...state.tickets,
            ticketCreating: true
        }
    })),

    on(SupportActions.CreateTicketSuccess, (state, action) => ({
        ...state,
        tickets: {
            ...state.tickets,
            active: state.tickets.active ? [action.payload.response, ...state.tickets.active] : [action.payload.response],
            ticketCreating: false
        }
    })),

    on(SupportActions.CreateTicketFailure, (state) => ({
        ...state,
        tickets: {
            ...state.tickets,
            ticketCreating: false
        }
    })),


    on(SupportActions.UpsertTicketWS, SupportActions.CloseTicketSuccess, SupportActions.ReopenTicketSuccess, (state, action) => {
        const activeStatuses = ['NEW', 'IN_PROGRESS'];
        const isActive = activeStatuses.includes(action.payload.response.status!);

        let active = state.tickets.active ? [...state.tickets.active] : [];
        let archive = {...state.tickets.archive};

        if (isActive) {
            active = [action.payload.response, ...active.filter(item => item.id !== action.payload.response.id)];
            archive = archivedTicketsAdapter.removeOne(action.payload.response.id!, archive);

        } else {
            active = active.filter(t => t.id !== action.payload.response.id);
            archive = archivedTicketsAdapter.upsertOne(action.payload.response, archive);

            const newId = action.payload.response.id!;
            archive = {
                ...archive,
                ids: [newId, ...archive.ids.filter(id => id !== newId)] as string[]
            };
        }

        return {
            ...state,
            tickets: {...state.tickets, active, archive},
        };
    }),


    on(SupportActions.SetRatingSuccess, (state, action) => {
        return {
            ...state,
            tickets: {
                ...state.tickets,
                archive: archivedTicketsAdapter.updateOne({id: action.payload.ticketId, changes: {rating: action.payload.rating, rateAvailable: false}}, state.tickets.archive),
            }
        };
    }),


    on(SupportActions.LoadLastMessageSuccess, (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            [action.payload.ticketId]: messagesAdapter.upsertOne(action.payload.message, state.messages[action.payload.ticketId])
        }
    })),


    on(SupportActions.DeleteTicketSuccess, (state, action) => {
        return {
            ...state,
            tickets: {
                ...state.tickets,
                active: state.tickets.active?.filter(ticket => ticket.id !== action.payload.ticketId),
                archive: archivedTicketsAdapter.removeOne(action.payload.ticketId, state.tickets.archive)
            }
        };
    }),


    on(SupportActions.MarkTicketAsReadSuccess, (state, action) => ({
        ...state,
        tickets: {
            ...state.tickets,
            active: state.tickets.active?.map(t =>
                t.id === action.payload.ticketId ? { ...t, unread: false } : t
            ),
            archive: {
                ...state.tickets.archive,
                entities: Object.fromEntries(
                    Object.entries(state.tickets.archive.entities).map(([id, t]) =>
                        id === String(action.payload.ticketId) ? [id, { ...t, unread: false }] : [id, t]
                    )
                ),
            },
        },
    })),


    on(SetAllInitialStates, () => ({...initialState}))
);

export const archivedTicketsSelector = archivedTicketsAdapter.getSelectors().selectAll;
export const messagesSelector = messagesAdapter.getSelectors().selectAll;


export const setImageDimsList = (messages: SupportTicketMobileMessageModel[]): SupportTicketMobileMessageModel[] => {
    const result: SupportTicketMobileModel[] = [];

    for (let i = 0; i < messages.length; i++) {
        if (messages[i]) {
            result.push(setImageDims(messages[i]));
        } else {
            result.push(messages[i]);
        }
    }

    return result;
};

export const setImageDims = (message: SupportTicketMobileMessageModel): SupportTicketMobileMessageModel => {
    const MAX_IMAGE_WIDTH = 400;

    if (message.attachments?.[0] && message.attachments[0].width && message.attachments[0].height) {
        const maxSide = message.attachments[0].width > message.attachments[0].height ? message.attachments[0].width : message.attachments[0].height;
        const multiplier = maxSide > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH : maxSide;

        return {
            ...message,
            attachments: [{
                ...message.attachments[0],
                width: message.attachments[0].width / maxSide * multiplier,
                height: message.attachments[0].height / maxSide * multiplier
            }]
        };
    }

    return message;
};
